import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapState } from "pinia";

import { useConvertToPendingPal } from "../../../store/useConvertToPendingPal";
import { IImmunizationRecord, PatientService } from "../../../../../../services/patient.service";
import { ToastrService } from "../../../../../../services/toastr.service";
import { IPageCallbackResult } from "../../../../../../resources-vue/vue-interfaces/i-page-container";
import { IPaginationNew } from "../../../../../../services/patient.service";
import { IGetImmuizationDeclineValueSet, LookupService } from "../../../../../../services/lookup-service";
import { PAGE_FAIL_RESPONSE } from "../../../../../../resources/elements/pagination";
import { TypeOfRecord } from "../../../../../../enums/TypeOfRecord";
import { ImmunizationStatus } from "../../../../../../enums/ImmunizationStatus";
import { EnumMap } from "../../../../../../common/enum-map";
import { Immunization } from "../../../../../../models/pal-health-history/immunzation.model";
import { DownloadService } from "../../../../../../services/download.service";

import VaccineRecordForm from "./VaccineRecordForm/VaccineRecordForm.vue";
import DeclinedVaccineForm from "./DeclinedVaccineForm/DeclinedVaccineForm.vue";
import FormSection from "../../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import Pagination from "../../../../../../resources-vue/vue-elements/Pagination/Pagination.vue";
import ErrorMessageHandler from "../../../../../../resources-vue/vue-custom-elements/ErrorMessageHandler/ErrorMessageHandler.vue";
import ImmunizationRow from "./ImmunizationRow/ImmunizationRow.vue";

export default defineComponent({
    components: {
        FormSection,
        Pagination,
        ErrorMessageHandler,
        ImmunizationRow,
        VaccineRecordForm,
        DeclinedVaccineForm
    },

    data(vm) {
        return {
            _lookupService: null,
            _toastrService: null,
            _patientsService: null,
            isDownloadInProgress: false as boolean,
            isAddVaccineRequested: false as boolean,
            isAddVaccineInProgress: false as boolean,
            isAddDeclineRequested: false as boolean,
            isAddDeclineInProgress: false as boolean,
            isLoading: false as boolean,
            immunizationPaginationInstance: null,
            page: null as IPaginationNew<IImmunizationRecord>,
            columns: 9 as number,
            isError: false as boolean,
            declinedValueSet: [] as IGetImmuizationDeclineValueSet[],
            typeOfRecordEnum: Array.from(TypeOfRecord.enumWithDescriptions.values()),
            statusEnum: Array.from(ImmunizationStatus.enumWithDescriptions.values()),
            immunizationStatusEnumMap: null,
            immunizationStatusEnum: null,
            typeOfRecordEnumMap: null,
            immunization: new Immunization(),
            declinedImmunization: new Immunization(),
            vaccineForm: null,
            _downloadService: null
        };
    },

    created() {
        this._lookupService = Container.instance.get(LookupService);
        this._downloadService = Container.instance.get(DownloadService);
        this._patientsService = Container.instance.get(PatientService);
        this._toastrService = Container.instance.get(ToastrService);
        this.immunizationPaginationInstance = this.$refs.immunizationPaginationInstance;
        this.vaccineForm = this.$refs.vaccineForm;
        this.immunizationStatusEnumMap = new EnumMap(this.statusEnum);
        this.typeOfRecordEnumMap = new EnumMap(this.typeOfRecordEnum);
        this.immunizationStatusEnum = [...this.statusEnum].filter(
            (option) => !this.immunizationStatusEnumMap.checkEnumValue("Declined", option.value)
        );
    },
    async mounted() {
        this.immunizationPaginationInstance = this.$refs.immunizationPaginationInstance;
        this.vaccineForm = this.$refs.vaccineForm;
        this.reset();
        let declinedValueSet = await this._lookupService.getImmunizationDeclineValueSet(this.application);
        this.declinedValueSet = declinedValueSet.data.map((valueSet: IGetImmuizationDeclineValueSet) => {
            let displayName = valueSet.DisplayName;
            displayName = displayName.charAt(0).toUpperCase() + displayName.slice(1);
            valueSet.DisplayName = displayName;
            return valueSet;
        });
    },
    computed: {
        ...mapState(useConvertToPendingPal, ["application", "accountId", "patientDetails", "patientId"]),
        loadingCount() {
            return this.page?.Items?.length > 0 ? this.page.Items.length : 3;
        },
        hasData() {
            return this.page?.ItemCount > 0;
        }
    },

    methods: {
        openAddVaccineRecord() {
            this.immunization = new Immunization({} as IImmunizationRecord);
            this.isAddVaccineRequested = true;
        },

        openAddDeclinedRecord() {
            this.declinedImmunization = new Immunization({} as IImmunizationRecord);
            this.isAddDeclineRequested = true;
        },

        async downloadVaccineRecords() {
            try {
                this.isDownloadInProgress = true;
                let response = await this._patientsService.downloadImmunization({
                    AccountId: this.accountId,
                    Application: this.application,
                    PatientId: this.patientId
                });
                this._downloadService.downloadFile(response);
            } catch (err) {
                console.error(err);
                this._toastrService.error({
                    title: "Download Failed",
                    message: `There was a problem while downloading the Vaccine Records.`
                });
            } finally {
                this.isDownloadInProgress = false;
            }
        },

        reset() {
            this.immunizationPaginationInstance?.reset();
        },
        async pageChangedCallback(pageNumber: number, pageSize: number): Promise<IPageCallbackResult> {
            try {
                this.isError = false;
                this.isLoading = true;
                let query = {
                    id: this.patientId,
                    accountId: this.accountId,
                    application: this.application,
                    page: pageNumber,
                    pageLength: pageSize
                };
                let data = await this._patientsService.getVaccineRecords(this.patientId, query);
                this.page = data.data;
                return {
                    totalPageCount: data.PageCount,
                    success: data.ItemCount > 0,
                    totalCount: data.ItemCount
                };
            } catch (e) {
                console.error(e);
                this.isError = true;
                return PAGE_FAIL_RESPONSE;
            } finally {
                this.isLoading = false;
            }
        },
        async saveNewVaccineRecord(openForm?: boolean) {
            const isFormValid = await (this.$refs.vaccineForm as any).v$.$validate();
            if (isFormValid) {
                try {
                    this.isAddVaccineInProgress = true;
                    const payload = {
                        Application: this.application,
                        AccountId: this.accountId,
                        PalliativePatientImmunization: this.immunization
                    };
                    await this._patientsService.createVaccineRecord(this.patientId, payload);
                    this.closeAddVaccineRecord();
                    this.reset();
                    this._toastrService.success({
                        title: `Vaccine Record Added`,
                        message: `The Vaccine record has been successfully added.`
                    });
                    if (openForm) {
                        this.openAddVaccineRecord();
                    }
                } catch (err) {
                    console.error(err);
                    this._toastrService.error({
                        title: "Error",
                        message: "There was an error creating a Record of Vaccine Administration. Please try again."
                    });
                } finally {
                    this.isAddVaccineInProgress = false;
                }
            } else {
                this._toastrService.error({
                    title: "Error -Vaccine Form",
                    message: "Please fill required fields"
                });
            }
        },

        closeAddVaccineRecord() {
            this.isAddVaccineRequested = false;
            this.vaccineForm?.resetSelectedVaccine();
        },

        async saveDeclinedVaccineRecord(openForm?: boolean) {
            const isFormValid = await (this.$refs.declinedVaccineForm as any).v$.$validate();
            if (isFormValid) {
                try {
                    this.isAddDeclineInProgress = true;
                    this.declinedImmunization.Status = this.immunizationStatusEnumMap.getEnumValue(
                        "Declined"
                    ) as number;
                    const payload = {
                        Application: this.application,
                        AccountId: this.accountId,
                        PalliativePatientImmunization: this.declinedImmunization
                    };
                    await this._patientsService.createVaccineRecord(this.patientId, payload);
                    this.closeAddDeclinedRecord();
                    this.reset();
                    this._toastrService.success({
                        title: `Declined Vaccine Record Added`,
                        message: `The Declined Vaccine record has been successfully added.`
                    });
                    if (openForm) {
                        this.openAddDeclinedRecord();
                    }
                } catch (err) {
                    console.error(err);
                    this._toastrService.error({
                        title: "Error",
                        message:
                            "There was an error creating a Record of Declined Vaccine Administration. Please try again."
                    });
                } finally {
                    this.isAddDeclineInProgress = false;
                }
            } else {
                this._toastrService.error({
                    title: "Error -Declined Vaccine Form",
                    message: "Please fill required fields"
                });
            }
        },
        closeAddDeclinedRecord() {
            this.isAddDeclineRequested = false;
        }
    }
});
